import { cn } from 'cn';

/**
 * The line every screen ends on.
 *
 * It carries the one thing this product must never let a reader forget: that
 * a translation here is a reading aid and not the publisher's own words. That
 * sentence used to live on exactly one screen — the walking skeleton nobody
 * sees any more — so it reached none of the pages that actually show
 * translated text.
 *
 * **Deliberately small.** The first version was a three-column block 269px
 * tall, which on a khutbah is a screenful of chrome after the thing someone
 * came to read, and on a short page leaves a hole beneath it. Quran.com's
 * footer is a strip, and a strip is right: this is a disclaimer and a few
 * links, not a site map. One row on a desktop, stacking only where it must.
 *
 * It goes INSIDE each screen's scrolling area rather than in the shell,
 * because half the screens scroll their own pane. A footer pinned in the
 * shell would hover above the khutbah permanently, which is the opposite of
 * a footer.
 */

const LINKS = [
  { href: '?screen=home', label: 'This Friday' },
  { href: '?screen=library', label: 'Library' },
  { href: '?screen=prepare', label: 'Prepare' },
  { href: '?screen=chat', label: 'Ask AI' },
  { href: '?screen=glossary', label: 'Glossary' },
  { href: '?screen=settings', label: 'Settings' },
] as const;

export function SiteFooter({ className }: { className?: string }) {
  return (
    // Full-bleed: a band across the window, belonging to neither the rail nor
    // the content. `bg-card` against the page's `bg-background` is what makes
    // it read as a separate region rather than as the end of the page — the
    // same device quran.com uses, white on grey.
    //
    // No `mt-auto` any more: the shell gives the screen above a `min-h-full`
    // so a short page already fills the window, and the footer follows it
    // rather than having to be pushed.
    <footer
      className={cn(
        'shrink-0 border-t border-border bg-card',
        'px-4 py-6 sm:px-6',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-7xl text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <a href="?screen=home" className="flex items-center">
          <img
            src="/brand/lockup.svg"
            alt="Almimbar"
            height={16}
            className="h-4 w-auto dark:hidden"
          />
          <img
            src="/brand/lockup-on-dark.svg"
            alt="Almimbar"
            height={16}
            className="hidden h-4 w-auto dark:block"
          />
        </a>

        <nav aria-label="Footer">
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {LINKS.map((link) => (
              <li key={link.href}>
                <a href={link.href} className="hover:text-foreground">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <span className="ms-auto">© {new Date().getFullYear()}</span>
      </div>

      {/*
        The standing declaration. Not small print to be got past, and the
        order is deliberate: the reader is told the content is AI-generated
        BEFORE being told how to tell reviewed from unreviewed, and told that
        unreviewed text can be wrong before being offered the way to report
        it. A disclaimer that buries "a model wrote this" behind a provenance
        vocabulary is a disclaimer written for us rather than for them.

        The Arabic is not included in this claim. It is the publisher's own
        text, recovered from their document; only the translations and the
        learning content are generated.
      */}
      <p className="mt-2 text-pretty">
        <strong className="font-semibold text-foreground">
          Translations and learning content here are generated by AI
        </strong>{' '}
        unless a passage says otherwise. Every passage carries its own mark, so
        you can see whether a person has reviewed it — and{' '}
        <strong className="font-semibold text-foreground">
          unreviewed text can contain mistakes, because a model produced it
        </strong>
        . If you find one, use <em>Report a problem</em> on that passage.
      </p>

      {/*
        The line that matters most once text leaves the screen. A reader
        checking a translation for themselves is one thing; a khateeb reading
        it from a minbar, or a post quoting it as a ministry's words, is
        another, and the product cannot follow it there.
      */}
      <p className="mt-1.5 text-pretty">
        <strong className="font-semibold text-foreground">
          Before you quote, print or publish anything from here, check it
          against the original
        </strong>{' '}
        for correctness and authenticity. We cannot take responsibility for how
        it is used.
      </p>

      <p className="mt-1.5 text-pretty">
        The Arabic is the publisher&rsquo;s own: khutbahs are published by Oman
        Awqaf and UAE Awqaf and reproduced with their provenance shown. This is
        an unofficial reading aid, not endorsed by any ministry, and the
        assistant answers only from a khutbah&rsquo;s own text and never gives
        a ruling.
      </p>

      {/*
        Why the product exists, in its own words. It belongs here rather than
        on a marketing page: it is the measure every decision in this codebase
        is made against, and a reader deciding whether to trust an unreviewed
        translation is helped by knowing what it was built to do.
      */}
      <p className="mt-1.5 text-pretty">
        Almimbar exists so that someone who does not read Arabic can follow the
        Friday khutbah they are sitting in — most of all across the Gulf, where
        the sermon is in Arabic and much of the congregation is not.
      </p>
      </div>
    </footer>
  );
}
